<template>
  <div class="content-wrapper">
    <Loading v-if="!loadStatus" text="Loading..." />
    <template v-if="loadStatus && !Object.keys(projectDetails).length">
      <h2 class="page-titles">{{ projectDetails.name }}</h2>
    </template>
    <template v-if="loadStatus && Object.keys(projectDetails).length">
      <div class="flex-center flex-wrap md:flex-nowrap margin-top-4">
        <div class="flex items-end">
          <h2 class="page-titles" v-show="!isInputDisabled">
            {{ projectDetails.name }}
          </h2>
          <input
            v-show="isInputDisabled"
            ref="nameInputRef"
            autofocus
            @keyup.enter="handleToggleInput"
            @blur="isInputDisabled = false"
            type="text"
            v-model="projectDetails.name"
            :disabled="!isInputDisabled"
            class="page-titles project-name-input border border-x-0 border-t-0 border-b border-primary outline-none max-w-80
                                            disabled:border-none
                                            "
          />
          <button
            v-show="!isInputDisabled"
            class="edit-icon"
            @click="handleToggleInput"
          >
            <img src="../../assets/edit.svg" alt="" />
          </button>
        </div>

        <div>
          <button
            class="finalize-button"
            :class="{ 'button-loading': finalizingOrder }"
            @click="handleModal"
            v-if="projectDetails.state === 'wish_list'"
          >
            Finalize
          </button>
        </div>
      </div>

      <div class="arrow-link" @click="doCopy">
        {{ trimText(projectDetails.recipientCollectionAdminOrderUrl, 40) }}
        <img src="../../assets/copy-icon.svg" alt="" />
      </div>

      <!-- Tabs Here -->
      <Tabs :tabs="tabs" @setActiveTab="setActiveTab($event)" />
      <transition name="slide-fade">
        <ProjectSettingsTab
          v-if="tabs[0].isActive"
          @subscribed="setActiveTab($event)"
        />

        <ProjectRecipientsList
          v-if="tabs[1].isActive"
          :project-details="projectDetails"
          :apparelSizeNeeded="projectDetails.apparelSizeNeeded"
          :projectRecipients="projectDetails.projectRecipients"
        />
      </transition>

      <!-- Modal Here -->
      <section>
        <FinalizeModal
          :showFooter="true"
          modalName="finalizeOrder"
          buttonLabel="Finalize"
          colorVariation="coast"
          v-show="isModalActive"
          @confirmAction="finalizeOrder"
        >
          <template slot="header">
            <h4 class="editorial header">Finalize Order</h4>
          </template>
          <template slot="body">
            <p class="modal-description">
              Once you finalize, all submissions will be sent to Gemnote for
              fulfillment and this link will no longer be accessible.
            </p>
          </template>
        </FinalizeModal>
      </section>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import DateFns from '@/mixins/DateFns'
import { progressStatus, trimText } from '../../helpers'
import ProjectSettingsTab from '@/components/Projects/ProjectSettingsTab'
import ProjectRecipientsList from '@/components/Projects/ProjectRecipientsList'
import FinalizeModal from '@/components/Universal/RemoveModal'

export default {
  name: 'ProjectDetails',
  mixins: [DateFns],
  props: ['id'],
  components: {
    ProjectSettingsTab,
    ProjectRecipientsList,
    Tabs: () => import('../Universal/Tabs'),
    FinalizeModal
  },
  async created() {
    await this.$store.dispatch(
      'project/getProjectDetail',
      this.$route.params.id
    )
  },
  data: () => ({
    whitelistedDomains: [],
    tabs: [
      { name: 'Settings', isActive: true },
      { name: 'Submissions', isActive: false }
    ],
    trimText: trimText,
    isInputDisabled: false
  }),
  computed: {
    ...mapGetters({
      activeModals: 'modal/activeModals'
    }),

    loadStatus() {
      return progressStatus(this.loadingProjectDetails)
    },

    isModalActive() {
      return this.activeModals('finalizeOrder')
    },

    ...mapState({
      toggleAddressCollection: state => state.project.toggleAddressCollection,
      loadingProjectDetails: state => state.project.loadingProjectDetails,
      projectDetails: state => state.project.projectDetails,
      updatingAllowedDomains: state => state.project.updatingAllowedDomains,
      finalizingOrder: state => state.project.finalizingOrder
    })
  },

  methods: {
    setActiveTab(selectedTab) {
      this.tabs.forEach(tab => {
        tab.isActive = tab.name === selectedTab.name
      })
    },

    enableDisableCollectionLink() {
      if (this.projectDetails.enableCollectionLink) {
        this.$store.dispatch('project/disableAddressCollectionLink', this.id)
      } else {
        this.$store.dispatch('project/enableAddressCollectionLink', this.id)
      }
    },
    doCopy() {
      if (this.projectDetails.recipientCollectionAdminOrderUrl) {
        this.$copyText(this.projectDetails.recipientCollectionAdminOrderUrl)
        this.$store.dispatch('notification/pushNotification', {
          type: 'success',
          messages: [`Link copied`]
        })
      }
    },

    updateWhitelistedDomain(domains) {
      this.$store.dispatch('project/updateWhitelistedDomain', domains)
    },

    validateEmail(value) {
      // eslint-disable-next-line no-useless-escape
      return /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(
        value
      )
    },

    async finalizeOrder() {
      await this.$store.dispatch('project/finalizeOrder')
      this.$store.dispatch('modal/hideRemoveModal', 'finalizeOrder')
    },

    handleModal() {
      this.$store.dispatch('modal/showRemoveModal', 'finalizeOrder')
    },
    handleToggleInput() {
      this.isInputDisabled = !this.isInputDisabled

      if (this.isInputDisabled) {
        this.$nextTick(() => {
          this.$refs.nameInputRef.focus()
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.arrow-link {
  cursor: pointer;
  margin-top: 0rem;
  border: none !important;
  text-align: left;
  padding-left: 0px;
  font-size: 0.813rem;
}

.page-titles {
  font-size: 1.5rem;
  text-align: left;
  margin: 0px;
  line-height: normal;
}

.header-content {
  background: inherit;
  border: 1px solid #000000;
  padding: 3rem 1.5rem;
  margin-top: 35px;

  .header-title {
    text-align: left;
    margin-bottom: 20px;

    h5 {
      font-family: $editorial;
      font-weight: normal;
      font-size: 1rem;
    }
  }
  .row {
    display: flex;
    flex-direction: row;
    .col:first-child {
      width: 35%;
    }

    .col:last-child {
      width: 62%;
    }
  }

  .content-row {
    margin-bottom: 1rem;
    display: flex;
    gap: 2rem;
    align-items: center;

    p {
      text-align: left;
      flex-basis: 35%;
      font-weight: bold;
      font-size: 14px;
    }

    span {
      opacity: 0.5;
      font-size: 14px;
    }
  }
}

.address-collection {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1rem;

  h5 {
    font-size: 14px !important;
  }
}

.box-head {
  h5 {
    text-align: left;
    margin-bottom: 1.5rem;
  }

  form {
    width: 65%;
    .form-group {
      margin-bottom: 0 !important;

      input {
        height: 50px !important;
        border: 1px solid $onyx !important;
        padding: 0.5rem 0.5rem !important;
      }
    }
  }
}

.allow-domains {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  .label {
    flex-basis: 30%;
    //flex-shrink: 0;
    //text-align: left;
    display: flex;
    align-items: center;
    text-align: left;
    font-size: 14px;
    font-weight: bold;

    div {
      display: flex;
    }

    h5 {
      padding-right: 10px;
    }
  }

  .domain-tag-input {
    flex-basis: 70%;
    flex-shrink: 0;
  }
}

.link-title {
  flex-basis: 30%;
  text-align: left;
}

.link-content {
  display: flex;
  justify-content: flex-start;
  flex-basis: 70%;

  span {
    border: 1px solid $main-6;
    text-align: justify;
    padding: 0.5rem 0.5rem;
    font-size: 12px;
  }

  button {
    min-width: 50px;
    border-top: 1px solid $main-6;
    border-bottom: 1px solid $main-6;
    border-right: 1px solid $main-6;
    display: flex;
    justify-content: center;
    align-items: center;

    &:disabled {
      cursor: not-allowed;

      img {
        cursor: not-allowed;
        pointer-events: none;
        width: 1.2rem;
      }
    }

    img {
      cursor: pointer;
      width: 1.2rem;
    }
  }
}

@media (max-width: 900px) {
  .header-body {
    .row {
      flex-direction: column;

      .col {
        width: 100% !important;
      }
    }
  }
  .box-details {
    width: 100%;
  }

  .link-title {
    flex-basis: 100%;
  }

  .link-content {
    width: 100%;
  }
}

.finalize-button {
  background: #24292c;
  color: $main-white;
  font-size: 1rem;
  font-weight: normal;
  font-size: 0.813rem;
  height: 2.5rem;
  text-transform: uppercase;
  border-radius: 40px;
  padding: 0 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
}

.header {
  font-size: 2rem;
  font-weight: 500;
}

.modal-description {
  color: $pebble;
  padding-top: 1.5rem;
  margin-bottom: 3rem;
  font-weight: normal;
}

.project-name-input {
  min-width: 400px;
  max-width: 400px;
}

@media (max-width: 682px) {
  .project-name-input {
    min-width: 100%;
    max-width: 100%;
  }
}
</style>
